/**
 * Cookie Consent Manager - Google Consent Mode v2
 * Handles banner display, geolocation-based GDPR enforcement,
 * consent storage, and GTM signal updates.
 */

const CONSENT_COOKIE_NAME  = 'bc_cookie_consent';
const CONSENT_COOKIE_DAYS  = 365;
const CONSENT_VERSION      = 'v2';

// EU country codes for GDPR enforcement
const EU_COUNTRIES = [
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU',
  'IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES',
  'SE','IS','LI','NO','GB','CH'
];

// ─── Cookie helpers ────────────────────────────────────────────────────────────

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  if (!match) return null;
  try { return JSON.parse(decodeURIComponent(match[1])); } catch(e) { return null; }
}

// ─── GTM Consent Signal update ────────────────────────────────────────────────

function updateConsentSignals(preferences) {
  if (typeof gtag !== 'function') return;

  const analyticsGranted      = preferences.analytics      ? 'granted' : 'denied';
  const marketingGranted      = preferences.marketing      ? 'granted' : 'denied';
  const personalizationGranted = preferences.personalization ? 'granted' : 'denied';

  gtag('consent', 'update', {
    'analytics_storage':       analyticsGranted,
    'ad_storage':               marketingGranted,
    'ad_user_data':             marketingGranted,
    'ad_personalization':       marketingGranted,
    'personalization_storage':  personalizationGranted,
    'functionality_storage':    'granted',
    'security_storage':         'granted'
  });

  // Push consent event to dataLayer for GTM triggers
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event:           'consent_updated',
    consent_analytics:       preferences.analytics,
    consent_marketing:       preferences.marketing,
    consent_personalization: preferences.personalization
  });
}

// ─── Banner / Modal UI ────────────────────────────────────────────────────────

function showBanner() {
  const banner = document.getElementById('cookie-consent-banner');
  if (banner) {
    banner.style.display = 'block';
    requestAnimationFrame(() => banner.classList.add('cookie-banner--visible'));
  }
}

function hideBanner() {
  const banner = document.getElementById('cookie-consent-banner');
  if (banner) {
    banner.classList.remove('cookie-banner--visible');
    setTimeout(() => { banner.style.display = 'none'; }, 400);
  }
}

function showModal() {
  const modal = document.getElementById('cookie-preferences-modal');
  if (modal) {
    modal.style.display = 'flex';
    requestAnimationFrame(() => modal.classList.add('cookie-modal--visible'));

    // Pre-fill toggles with saved preferences
    const saved = getCookie(CONSENT_COOKIE_NAME);
    if (saved) {
      const analyticsEl      = document.getElementById('consent-analytics');
      const marketingEl      = document.getElementById('consent-marketing');
      const personalizationEl = document.getElementById('consent-personalization');
      if (analyticsEl)       analyticsEl.checked      = !!saved.analytics;
      if (marketingEl)       marketingEl.checked       = !!saved.marketing;
      if (personalizationEl) personalizationEl.checked = !!saved.personalization;
    }
  }
}

function hideModal() {
  const modal = document.getElementById('cookie-preferences-modal');
  if (modal) {
    modal.classList.remove('cookie-modal--visible');
    setTimeout(() => { modal.style.display = 'none'; }, 300);
  }
}

// ─── Save consent ────────────────────────────────────────────────────────────

function saveConsent(preferences) {
  const consentData = {
    version:         CONSENT_VERSION,
    timestamp:       new Date().toISOString(),
    analytics:       !!preferences.analytics,
    marketing:       !!preferences.marketing,
    personalization: !!preferences.personalization
  };

  setCookie(CONSENT_COOKIE_NAME, consentData, CONSENT_COOKIE_DAYS);
  updateConsentSignals(consentData);
  hideBanner();
  hideModal();
  showPreferencesTrigger();
}

function acceptAll() {
  saveConsent({ analytics: true, marketing: true, personalization: true });
}

function rejectAll() {
  saveConsent({ analytics: false, marketing: false, personalization: false });
}

function saveFromModal() {
  const analyticsEl       = document.getElementById('consent-analytics');
  const marketingEl       = document.getElementById('consent-marketing');
  const personalizationEl = document.getElementById('consent-personalization');

  saveConsent({
    analytics:       analyticsEl       ? analyticsEl.checked      : false,
    marketing:       marketingEl       ? marketingEl.checked       : false,
    personalization: personalizationEl ? personalizationEl.checked : false
  });
}

// ─── Preferences trigger (footer link) ───────────────────────────────────────

function showPreferencesTrigger() {
  const trigger = document.getElementById('cookie-preferences-trigger');
  if (trigger) trigger.style.display = 'flex';
}

// ─── Geolocation check ───────────────────────────────────────────────────────

async function isEUUser() {
  try {
    // Use a lightweight IP geolocation API (no key required, GDPR-safe)
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(3000)
    });
    if (!response.ok) return true; // Default to true (GDPR-safe fallback)
    const data = await response.json();
    return EU_COUNTRIES.includes(data.country_code);
  } catch (e) {
    return true; // Default to GDPR-compliant if geolocation fails
  }
}

// ─── Main initialization ─────────────────────────────────────────────────────

async function initCookieConsent() {
  const saved = getCookie(CONSENT_COOKIE_NAME);

  // If user already consented, just restore their signals and show trigger
  if (saved && saved.version === CONSENT_VERSION) {
    updateConsentSignals(saved);
    showPreferencesTrigger();
    return;
  }

  // Determine if we need to show the banner
  // For EU users: mandatory full banner
  // For non-EU users: show banner anyway (simplifies compliance globally)
  const euUser = await isEUUser();

  // Always show banner - either GDPR mandatory or best-practice globally
  showBanner();

  // For non-EU users, auto-accept after 30 seconds if they don't interact
  // (optional UX improvement - comment out if not desired)
  if (!euUser) {
    setTimeout(() => {
      const stillVisible = document.getElementById('cookie-consent-banner');
      if (stillVisible && stillVisible.classList.contains('cookie-banner--visible')) {
        // Non-EU: grant analytics by default, keep marketing denied
        saveConsent({ analytics: true, marketing: false, personalization: false });
      }
    }, 30000);
  }
}

// ─── Event listeners ─────────────────────────────────────────────────────────

function attachConsentListeners() {
  const acceptAllBtn         = document.getElementById('cookie-accept-all');
  const rejectAllBtn         = document.getElementById('cookie-reject-all');
  const manageBtn            = document.getElementById('cookie-manage');
  const modalClose           = document.getElementById('cookie-modal-close');
  const modalOverlay         = document.getElementById('cookie-modal-overlay');
  const savePreferencesBtn   = document.getElementById('cookie-save-preferences');
  const acceptAllModalBtn    = document.getElementById('cookie-accept-all-modal');
  const cookieFab = document.getElementById('cookie-preferences-trigger');

  if (acceptAllBtn)   acceptAllBtn.addEventListener('click', acceptAll);
  if (rejectAllBtn)   rejectAllBtn.addEventListener('click', rejectAll);
  if (manageBtn)      manageBtn.addEventListener('click', () => { hideBanner(); showModal(); });
  if (modalClose)     modalClose.addEventListener('click', hideModal);
  if (modalOverlay)   modalOverlay.addEventListener('click', hideModal);
  if (savePreferencesBtn) savePreferencesBtn.addEventListener('click', saveFromModal);
  if (acceptAllModalBtn)  acceptAllModalBtn.addEventListener('click', acceptAll);
  if (cookieFab)      cookieFab.addEventListener('click', () => showModal());


  // Keyboard accessibility - close modal on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideModal();
  });
}

// ─── Boot ─────────────────────────────────────────────────────────────────────

// Must run on every Turbolinks navigation to re-attach listeners
document.addEventListener('turbolinks:load', () => {
  attachConsentListeners();
  // Only initialize (show banner check) once per page session
  if (!window._consentInitialized) {
    window._consentInitialized = true;
    initCookieConsent();
  } else {
    // On subsequent navigations: restore signals if consent exists
    const saved = getCookie(CONSENT_COOKIE_NAME);
    if (saved && saved.version === CONSENT_VERSION) {
      updateConsentSignals(saved);
      showPreferencesTrigger();
    }
  }
});
